import React, { useState } from 'react';
import { NavBar } from '../kuroco/AppBars';
import { EditSaveBar, EditToast } from '../kuroco/EditHelpers';
import { useSettingsData } from './useSettingsData';
import { useNavigateWithFilters } from '../../utils/dashboardNav';
import { CADENCES } from '../../constants/cadences';
import { MAX_PLAN_ACTIONS, actionTargetLabel, goalsActionTargetKeys } from '../../utils/actionTargets';

export default function ActionEditScreen() {
  const navigate = useNavigateWithFilters();
  const { goals, submitActions, D } = useSettingsData();
  // Keep the whole action (actionId, いつ/何を/どのくらい) — only name/cadence are edited here
  const [acts, setActs] = useState(() =>
    (goals?.actions?.length ? goals.actions : D.actions).map((a) => ({
      ...a,
      name: a.name || '',
      cadence: a.cadence || CADENCES[2],
    }))
  );
  // 行動プランの指標 from the saved goals (追加KGI → 注力①②③)
  const targetKeys = goalsActionTargetKeys(goals);
  const [toast, setToast] = useState(false);
  const [toastText, setToastText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const setName = (i, v) => setActs((p) => p.map((a, j) => (j === i ? { ...a, name: v } : a)));
  const setCad = (i, v) => setActs((p) => p.map((a, j) => (j === i ? { ...a, cadence: v } : a)));
  const setFocus = (i, k) => setActs((p) => p.map((a, j) => (j === i ? { ...a, focusKey: k } : a)));
  const remove = (i) => setActs((p) => p.filter((_, j) => j !== i));
  // With a single 指標 a new row is for it; with several the stylist picks
  const add = () =>
    setActs((p) => [
      ...p,
      { id: Date.now(), name: '', cadence: CADENCES[2], focusKey: targetKeys.length === 1 ? targetKeys[0] : null },
    ]);
  const move = (i, dir) =>
    setActs((p) => {
      const n = [...p];
      const j = i + dir;
      if (j < 0 || j >= n.length) return p;
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });

  // → 確認中. The live plan changes only when a manager approves, so no optimistic
  // write and no changelog line here (the approve step writes it).
  const save = async () => {
    if (saving) return;
    const normalized = acts
      .filter((a) => a.name.trim())
      .map((a) => ({
        actionId: a.actionId,
        name: a.name.trim(),
        cadence: a.cadence,
        when: a.when,
        what: a.what,
        howMuch: a.howMuch,
        focusKey: a.focusKey || null,
      }));
    if (!normalized.length) return;
    // focusKey counts as a change: re-tagging an action must reach the server
    const planKey = (list) =>
      JSON.stringify((list || []).map((a) => [a.name, a.cadence ?? null, a.focusKey ?? null]));
    if (planKey(normalized) === planKey(goals?.actions)) {
      navigate('/settings'); // nothing changed — don't send the manager an empty approval
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await submitActions(normalized);
      // Managers decide their own plan — the server returns it already approved.
      setToastText(
        res?.submission?.status === 'approved' ? '行動プランを保存しました' : '送信しました（マネージャー確認中）'
      );
    } catch (err) {
      setSaveError(err?.message || '保存に失敗しました');
      setSaving(false);
      return;
    }
    setToast(true);
    setTimeout(() => {
      setToast(false);
      navigate('/settings');
    }, 1100);
  };

  return (
    <div className="app">
      <NavBar title="行動プランの変更" backLabel="設定" backTo="/settings" hideSettings />
      <div className="scroll fade" style={{ paddingBottom: 110 }}>
        <div className="edit-hero edit-hero--act">
          <span className="edit-hero-ic">✅</span>
          <div className="edit-hero-t">毎日の行動プラン</div>
          <div className="edit-hero-s2">
            目標達成のために、毎日続ける行動を設定します。
            <br />
            「できた／できなかった」で記録できます。
          </div>
        </div>

        {acts.length === 0 && (
          <div style={{ padding: '12px 20px', fontSize: '13px', color: 'var(--muted)' }}>
            行動が未設定です。下のボタンから追加してください。
          </div>
        )}

        {acts.map((a, i) => (
          <div className="acard" key={a.id}>
            <div className="acard-head">
              <span className="acard-num">{i + 1}</span>
              <div className="acard-reorder">
                <button type="button" className="acard-mv" onClick={() => move(i, -1)} disabled={i === 0} aria-label="上へ">
                  ▲
                </button>
                <button type="button" className="acard-mv" onClick={() => move(i, 1)} disabled={i === acts.length - 1} aria-label="下へ">
                  ▼
                </button>
              </div>
              <button type="button" className="acard-del" onClick={() => remove(i)} aria-label="削除">
                削除
              </button>
            </div>
            <input className="acard-name" value={a.name} placeholder="行動の内容を入力" onChange={(e) => setName(i, e.target.value)} />
            <div className="acard-cad-label">実施タイミング</div>
            <div className="acard-cads">
              {(a.cadence && !CADENCES.includes(a.cadence) ? [...CADENCES, a.cadence] : CADENCES).map((c) => (
                <button key={c} type="button" className={`acard-cad${a.cadence === c ? ' is-on' : ''}`} onClick={() => setCad(i, c)}>
                  {c}
                </button>
              ))}
            </div>
            {targetKeys.length > 0 && (
              <>
                <div className="acard-cad-label">指標</div>
                <div className="acard-cads">
                  {[...targetKeys, null].map((k) => (
                    <button
                      key={k || 'none'}
                      type="button"
                      className={`acard-cad${(a.focusKey || null) === k ? ' is-on' : ''}`}
                      onClick={() => setFocus(i, k)}
                    >
                      {k ? actionTargetLabel(k, goals?.subKgiKey) : '指標なし'}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}

        {acts.length < MAX_PLAN_ACTIONS && (
          <button type="button" className="aadd" onClick={add}>
            <span className="aadd-ic">＋</span>行動を追加
          </button>
        )}

        <div className="edit-note">
          行動は1〜5個まで設定できます。{targetKeys.length >= 2 ? '指標ごとに1つが目安です。' : ''}保存するとマネージャーの確認（確認中）に送られ、確定すると変更履歴に記録されます。
        </div>
      </div>

      {saveError && <div className="edit-error" role="alert">{saveError}</div>}
      <EditSaveBar onSave={save} />
      <EditToast show={toast} text={toastText} />
    </div>
  );
}
